type Translate = (key: string) => string;

export interface ClinicalRecordPrintPatient {
  patient_name: string;
  species: string | null;
  breed: string | null;
  sex_label: string | null;
  age_label: string | null;
  weight_label: string | null;
  pet_record_code: string | null;
  owner_name: string | null;
  owner_phone: string | null;
  vet_name: string;
  vet_license: string | null;
  visit_date: string;
  visit_folio: string;
}

export interface ClinicalRecordAnamnesis {
  chief_complaint?: string | null;
  symptom_onset_days?: number | null;
  previous_illnesses?: string | null;
  current_medications?: string | null;
  vaccination_status?: string | null;
  deworming_status?: string | null;
  diet_notes?: string | null;
  environment_notes?: string | null;
  other_notes?: string | null;
  surgeries_notes?: string | null;
  last_heat_notes?: string | null;
  barf_diet_notes?: string | null;
  fleas_ticks_notes?: string | null;
  cohabitation_notes?: string | null;
  street_exposure_notes?: string | null;
}

export interface ClinicalRecordExam {
  body_temperature_celsius_tenths?: number | null;
  heart_rate_bpm?: number | null;
  respiratory_rate_bpm?: number | null;
  mucous_membranes?: string | null;
  hydration_status?: string | null;
  consciousness_level?: string | null;
  body_condition_score?: number | null;
  cardiovascular_notes?: string | null;
  respiratory_notes?: string | null;
  abdomen_notes?: string | null;
  lymph_node_notes?: string | null;
  capillary_refill_time?: string | null;
  cough_reflex?: string | null;
  gag_reflex?: string | null;
  pulse_description?: string | null;
  auscultation_percussion_notes?: string | null;
  general_impression?: string | null;
}

export interface ClinicalRecordDiagnosis {
  diagnosis_kind: string;
  description: string;
  is_primary?: number | boolean | null;
  prognosis?: string | null;
}

export interface ClinicalRecordPlan {
  treatment_notes?: string | null;
  medications_notes?: string | null;
  diet_recommendations?: string | null;
  activity_restrictions?: string | null;
  follow_up_notes?: string | null;
  owner_instructions?: string | null;
}

export interface ClinicalRecordAddendum {
  content: string;
  created_at: string;
}

export interface ClinicalRecordPrintInput {
  patient: ClinicalRecordPrintPatient;
  anamnesis: ClinicalRecordAnamnesis | null;
  exam: ClinicalRecordExam | null;
  diagnoses: ClinicalRecordDiagnosis[];
  plan: ClinicalRecordPlan | null;
  addendums: ClinicalRecordAddendum[];
}

const LOGO_PATH = '/novapet_logo_ticket.png';
let cached_logo_data_uri: string | null | undefined;

function esc(value: unknown): string {
  return String(value ?? '').replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

function dash(value: string | number | null | undefined): string {
  if (value == null) return '—';
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? esc(trimmed) : '—';
}

function row(label: string, value: string | number | null | undefined): string {
  return `<p class="row"><span class="lbl">${esc(label)}:</span>${dash(value)}</p>`;
}

function formatTempLabel(tenths: number | null | undefined): string | null {
  if (tenths == null) return null;
  return `${(tenths / 10).toFixed(1)} °C`;
}

async function resolveLogoDataUri(): Promise<string | null> {
  if (cached_logo_data_uri !== undefined) return cached_logo_data_uri;
  try {
    const response = await fetch(LOGO_PATH, { cache: 'force-cache' });
    if (!response.ok) {
      cached_logo_data_uri = null;
      return null;
    }
    const blob = await response.blob();
    cached_logo_data_uri = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('logo_read_failed'));
      reader.onload = () => {
        if (typeof reader.result === 'string') resolve(reader.result);
        else reject(new Error('logo_read_failed'));
      };
      reader.readAsDataURL(blob);
    });
    return cached_logo_data_uri;
  } catch {
    cached_logo_data_uri = null;
    return null;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function waitForPopupImages(popup: Window): Promise<void> {
  const images = Array.from(popup.document.images ?? []);
  if (images.length === 0) return;
  await Promise.race([
    Promise.all(
      images.map((image) =>
        image.complete
          ? Promise.resolve()
          : new Promise<void>((resolve) => {
              image.addEventListener('load', () => resolve(), { once: true });
              image.addEventListener('error', () => resolve(), { once: true });
            }),
      ),
    ).then(() => undefined),
    delay(1400),
  ]);
}

// Vacunas/Desparasitaciones are open free text now (not an enum) — print the dictated/typed
// value as-is, same as E. Mental/DH in the EFG. Cirugías/Último celo/BARF/pulgas-garrapatas/
// convivencia/exposición a la calle all have real backing fields now (previously the first two
// were print-only stubs hardcoded to `null`, and the "Otros" row crammed 3 concepts into 2
// combined fields — split apart per the Historia Clínica restructuring, 2026-07-11).
function buildHistoriaClinicaHtml(a: ClinicalRecordAnamnesis | null): string {
  return `
    <div class="historia-grid">
      <div class="col">
        ${row('Vacunas', a?.vaccination_status)}
        ${row('Desparasitaciones', a?.deworming_status)}
        ${row('Cirugías', a?.surgeries_notes)}
        ${row('Último celo', a?.last_heat_notes)}
      </div>
      <div class="col">
        <p class="otros-title">Otros:</p>
        ${row('Dieta del paciente', a?.diet_notes)}
        ${row('Consumo de dieta BARF', a?.barf_diet_notes)}
        ${row('Antec. pulgas/garrapatas', a?.fleas_ticks_notes)}
        ${row('Convivencia con otros perros', a?.cohabitation_notes)}
        ${row('Exposición a la calle', a?.street_exposure_notes)}
      </div>
    </div>`;
}

function buildAnamnesisHtml(a: ClinicalRecordAnamnesis | null, t: Translate): string {
  const lines = [
    row(t('clinical.f_chief_complaint'), a?.chief_complaint),
    row(t('clinical.f_symptom_onset_days'), a?.symptom_onset_days),
    row(t('clinical.f_previous_illnesses'), a?.previous_illnesses),
    row(t('clinical.f_current_medications'), a?.current_medications),
    row(t('clinical.f_other_notes'), a?.other_notes),
  ].join('');
  return `
    <div class="anamnesis-box">
      <p class="hint">Descripción de lo que le pasó al paciente de acuerdo a la historia del propietario.</p>
      ${lines}
    </div>`;
}

// Weight already renders in the patient-info table above (`row('Peso', patient.weight_label)`)
// so it's not duplicated inside the EFG grid — that 16th cell is used for SD (Sonidos Cardiacos)
// instead, completing the exact field set requested for the EFG restructuring.
function buildEfgHtml(e: ClinicalRecordExam | null, t: Translate): string {
  // E. Mental / DH are open free text now (not an enum) — print the dictated/typed value as-is.
  // MM keeps its fixed option list, so it still resolves through the enum-label i18n keys.
  const consciousness = e?.consciousness_level ?? null;
  const mucous = e?.mucous_membranes ? t(`clinical.mucous_membranes_${e.mucous_membranes}`) : null;
  const hydration = e?.hydration_status ?? null;
  const heart_rate = e?.heart_rate_bpm != null ? `${e.heart_rate_bpm} lpm` : null;
  const resp_rate = e?.respiratory_rate_bpm != null ? `${e.respiratory_rate_bpm} rpm` : null;
  const temp = formatTempLabel(e?.body_temperature_celsius_tenths);
  const body_condition = e?.body_condition_score != null ? `${e.body_condition_score}/9` : null;

  return `
    <div class="efg-grid">
      ${row('E. Mental', consciousness)}
      ${row('CC', body_condition)}
      ${row('DH', hydration)}
      ${row('SD', e?.cardiovascular_notes)}

      ${row('MM', mucous)}
      ${row('TLLC', e?.capillary_refill_time)}
      ${row('RT', e?.cough_reflex)}
      ${row('RD', e?.gag_reflex)}

      ${row('FC', heart_rate)}
      ${row('Pulso', e?.pulse_description)}
      ${row('FR', resp_rate)}
      ${row('PA', e?.abdomen_notes)}

      ${row('LN', e?.lymph_node_notes)}
      ${row('PP', e?.auscultation_percussion_notes)}
      ${row('CP', e?.respiratory_notes)}
      ${row('T°', temp)}
    </div>
    <div class="otros-hallazgos">
      <p class="lbl">Otros hallazgos:</p>
      <p class="findings">${dash(e?.general_impression ?? null)}</p>
    </div>`;
}

function buildDiagnosesHtml(diagnoses: ClinicalRecordDiagnosis[], kind: string, t: Translate): string {
  const matches = diagnoses.filter((d) => d.diagnosis_kind === kind);
  if (matches.length === 0) return `<p class="empty">${esc(t('clinical.no_diagnoses'))}</p>`;
  return `<ul class="diag-list">${matches
    .map((d) => {
      const primary = d.is_primary ? '★ ' : '';
      const prognosis = d.prognosis ? ` — Pronóstico: ${esc(t(`clinical.prognosis_${d.prognosis}`))}` : '';
      return `<li>${primary}${esc(d.description)}${prognosis}</li>`;
    })
    .join('')}</ul>`;
}

function buildOtherDiagnosesHtml(diagnoses: ClinicalRecordDiagnosis[], t: Translate): string {
  const others = diagnoses.filter((d) => d.diagnosis_kind !== 'differential' && d.diagnosis_kind !== 'presumptive');
  if (others.length === 0) return '';
  return `<p class="others-note"><strong>Otros:</strong> ${others
    .map((d) => `${esc(t(`clinical.diagnosis_kind_${d.diagnosis_kind}`))}: ${esc(d.description)}`)
    .join(' · ')}</p>`;
}

function buildPlanHtml(p: ClinicalRecordPlan | null, t: Translate): string {
  const rows = [
    ['treatment_notes', p?.treatment_notes],
    ['medications_notes', p?.medications_notes],
    ['diet_recommendations', p?.diet_recommendations],
    ['activity_restrictions', p?.activity_restrictions],
    ['follow_up_notes', p?.follow_up_notes],
    ['owner_instructions', p?.owner_instructions],
  ] as const;
  const has_any = rows.some(([, v]) => (v ?? '').toString().trim().length > 0);
  if (!has_any) return `<p class="empty">Sin plan de tratamiento registrado.</p>`;
  return rows
    .filter(([, v]) => (v ?? '').toString().trim().length > 0)
    .map(([key, v]) => row(t(`clinical.f_${key}`), v))
    .join('');
}

function buildObservacionesHtml(other_notes: string | null | undefined, addendums: ClinicalRecordAddendum[]): string {
  const parts: string[] = [];
  if (other_notes && other_notes.trim().length > 0) {
    parts.push(`<p class="row">${esc(other_notes)}</p>`);
  }
  for (const ad of addendums) {
    const date = new Date(ad.created_at).toLocaleDateString();
    parts.push(`<p class="addendum"><strong>Addenda (${esc(date)}):</strong> ${esc(ad.content)}</p>`);
  }
  if (parts.length === 0) return `<p class="empty">Sin observaciones adicionales.</p>`;
  return parts.join('');
}

function buildDocumentHtml(input: ClinicalRecordPrintInput, t: Translate, logo_data_uri: string | null): string {
  const { patient, anamnesis, exam, diagnoses, plan, addendums } = input;
  const logo_markup = logo_data_uri
    ? `<img class="logo" src="${esc(logo_data_uri)}" alt="Novapet" />`
    : '<p class="logo-fallback">NOVAPET</p>';

  return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Expediente clínico — ${esc(patient.patient_name)}</title>
<style>
  * { box-sizing: border-box; }
  body {
    font-family: Arial, Helvetica, sans-serif;
    color: #17313a;
    margin: 0;
    padding: 10mm;
    background: #fff;
  }
  .page { page-break-after: always; }
  .page:last-child { page-break-after: auto; }
  .sheet {
    border: 2px solid #1f6d82;
    border-radius: 14px;
    padding: 14px 18px 18px;
  }
  .top-row {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }
  .logo { height: 56px; width: auto; }
  .logo-fallback { font-size: 18px; font-weight: 800; color: #1f6d82; margin: 0; }
  .center-title { text-align: center; }
  .center-title .brand { font-size: 16px; font-weight: 800; letter-spacing: 0.5px; margin: 0; color: #17313a; }
  .center-title .tagline { font-size: 11px; font-weight: 700; margin: 1px 0; color: #1f6d82; text-transform: uppercase; }
  .center-title .doc-title { font-size: 12px; font-weight: 700; margin: 2px 0 0; text-transform: uppercase; letter-spacing: 0.4px; }
  .exp-box {
    border: 1px solid #1f6d82;
    border-radius: 8px;
    padding: 6px 10px;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    color: #1f6d82;
  }
  .tutor-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 12.5px;
    padding: 6px 0;
    border-bottom: 1px solid #1f6d82;
    margin-bottom: 8px;
  }
  .section-title {
    font-size: 12px;
    font-weight: 800;
    color: #1f6d82;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin: 10px 0 4px;
  }
  .resena-grid, .efg-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 3px 18px;
    font-size: 12px;
  }
  .efg-grid { grid-template-columns: repeat(4, 1fr); }
  .row { margin: 2px 0; }
  .lbl { color: #1f6d82; font-weight: 700; margin-right: 4px; }
  .historia-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 20px; font-size: 12px; }
  .otros-title { font-weight: 700; color: #1f6d82; margin: 0 0 2px; font-size: 11.5px; text-transform: uppercase; }
  .anamnesis-box { font-size: 12px; border: 1px solid #cfe3e8; border-radius: 8px; padding: 8px 10px; }
  .anamnesis-box .hint { font-style: italic; color: #556; font-size: 10.5px; margin: 0 0 6px; }
  .otros-hallazgos { margin-top: 6px; font-size: 12px; }
  .otros-hallazgos .findings { margin: 2px 0 0; }
  .box {
    border: 2px solid #1f6d82;
    border-radius: 12px;
    padding: 10px 16px 14px;
    margin-bottom: 14px;
    font-size: 12px;
  }
  .box.tall { min-height: 90px; }
  .box-title {
    text-align: center;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.4px;
    color: #1f6d82;
    background: #eaf4f6;
    margin: -10px -16px 10px;
    padding: 6px 16px;
    border-radius: 10px 10px 0 0;
    text-transform: uppercase;
  }
  .diag-list { margin: 0; padding-left: 18px; }
  .diag-list li { margin-bottom: 3px; }
  .others-note { margin-top: 6px; font-size: 11px; color: #444; }
  .addendum { font-size: 11.5px; color: #333; margin: 4px 0; }
  .empty { color: #777; font-style: italic; margin: 0; }
  .medico-a-cargo {
    margin-top: 20px;
    padding-top: 8px;
    border-top: 1px solid #1f6d82;
    font-size: 12.5px;
    font-weight: 700;
  }
  @media print {
    body { padding: 6mm; }
    @page { margin: 6mm; }
  }
</style>
</head>
<body>
  <div class="page page-1">
    <div class="sheet">
      <div class="top-row">
        <div class="logo-wrap">${logo_markup}</div>
        <div class="center-title">
          <p class="brand">NOVAPET</p>
          <p class="tagline">Hospital Veterinario de Especialidades</p>
          <p class="doc-title">Expediente Clínico</p>
        </div>
        <div class="exp-box">No. Expediente: ${dash(patient.pet_record_code)}</div>
      </div>

      <div class="tutor-row">
        <span><span class="lbl">Tutor/Propietario:</span>${dash(patient.owner_name)}</span>
        <span><span class="lbl">Fecha:</span>${esc(patient.visit_date)}</span>
      </div>

      <p class="section-title">Reseña</p>
      <div class="resena-grid">
        ${row('Paciente', patient.patient_name)}
        ${row('Sexo', patient.sex_label)}
        ${row('Raza', patient.breed)}
        ${row('Especie', patient.species)}
        ${row('Edad', patient.age_label)}
        ${row('Peso', patient.weight_label)}
        ${row('Teléfono', patient.owner_phone)}
        ${row('Expediente', patient.pet_record_code)}
      </div>

      <p class="section-title">Historia Clínica</p>
      ${buildHistoriaClinicaHtml(anamnesis)}

      <p class="section-title">Anamnesis</p>
      ${buildAnamnesisHtml(anamnesis, t)}

      <p class="section-title">EFG: Examen Físico General</p>
      ${buildEfgHtml(exam, t)}
    </div>
  </div>

  <div class="page page-2">
    <div class="box">
      <p class="box-title">Diagnósticos Diferenciales</p>
      ${buildDiagnosesHtml(diagnoses, 'differential', t)}
    </div>
    <div class="box">
      <p class="box-title">Diagnósticos Presuntivos</p>
      ${buildDiagnosesHtml(diagnoses, 'presumptive', t)}
      ${buildOtherDiagnosesHtml(diagnoses, t)}
    </div>
    <div class="box tall">
      <p class="box-title">Plan y Tratamiento</p>
      ${buildPlanHtml(plan, t)}
    </div>
    <div class="box tall">
      <p class="box-title">Observaciones</p>
      ${buildObservacionesHtml(anamnesis?.other_notes, addendums)}
    </div>

    <div class="medico-a-cargo">
      <p class="row">Médico a cargo: ${esc(patient.vet_name)}</p>
      <p class="row">Céd. Prof.: ${dash(patient.vet_license)}</p>
    </div>
  </div>
</body>
</html>`;
}

/**
 * Builds the same print-ready clinical-record document (2 pages, matching the hospital's
 * paper "Expediente clínico" form) as a standalone HTML string — shared by `printClinicalRecord`
 * (below) and by the inline `<iframe srcDoc={...}>` preview in `ClinicalHistoryModal.tsx`, so
 * both surfaces are always byte-for-byte the same document.
 */
export async function buildClinicalRecordHtml(
  input: ClinicalRecordPrintInput,
  t: Translate,
): Promise<string> {
  const logo_data_uri = await resolveLogoDataUri();
  return buildDocumentHtml(input, t, logo_data_uri);
}

/**
 * Opens the print-ready clinical-record document in a popup and triggers the browser print
 * dialog — same no-external-dependency approach as `prescription-print.ts` (the user picks
 * "Guardar como PDF" as the destination). Returns false if the popup was blocked.
 */
export async function printClinicalRecord(input: ClinicalRecordPrintInput, t: Translate): Promise<boolean> {
  const html = await buildClinicalRecordHtml(input, t);

  const w = window.open('', '_blank', 'width=820,height=960');
  if (!w) return false;
  w.opener = null;
  w.document.open();
  w.document.write(html);
  w.document.close();
  await waitForPopupImages(w);
  w.focus();
  w.print();
  return true;
}
